import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import FaceAvatar from '../common/FaceAvatar';
import { useLanguage } from '../context/LanguageContext';
import { trackEvent } from '../utils/analytics';
import * as FiIcons from 'react-icons/fi';
const { FiX, FiExternalLink } = FiIcons;

const Pricing = () => {
  const { t } = useLanguage();
  const [showComparison, setShowComparison] = useState(false);
  const [isEnterpriseModalOpen, setIsEnterpriseModalOpen] = useState(false);
  const [currency, setCurrency] = useState('EUR');

  useEffect(() => {
    if (isEnterpriseModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isEnterpriseModalOpen]);

  // Plan prices are stored in EUR. Rates refreshed Oct 2026, adjusted up to the next X.99 / X.X9 price point.
  const RATES = { EUR: 1, USD: 1.1215, AUD: 1.6056 };
  const SYMBOLS = { EUR: '€', USD: '$', AUD: 'A$' };
  const rate = RATES[currency];
  const symbol = SYMBOLS[currency];

  // Plans: round up to the next whole number minus 1 cent (e.g. 22.42 -> 22.99)
  const planPrice = (eur) => {
    if (parseFloat(eur) === 0) return '0';
    return (Math.ceil(eur * rate) - 0.01).toFixed(2);
  };
  // Fees: round the fixed component up to the next X9 cents (e.g. 0.77 -> 0.79)
  const feeAmount = (eur) => {
    if (!eur) return null;
    const cents = eur * rate * 100;
    return ((Math.ceil(cents / 10) * 10 - 1) / 100).toFixed(2);
  };
  const txFee = (pct, eur) => {
    const fee = feeAmount(eur);
    return fee ? `${pct}% + ${symbol}${fee}` : `${pct}%`;
  };

  const plans = [
    {
      name: t("pricingSection.plans.free.name"),
      desc: t("pricingSection.plans.free.desc"),
      price: "0",
      features: [
        t("pricingSection.plans.free.feat1"),
        t("pricingSection.plans.free.feat2"),
        t("pricingSection.plans.free.feat3"),
        t("pricingSection.plans.free.feat4"),
        t("pricingSection.plans.free.feat5"),
        t("pricingSection.plans.free.feat6")
      ],
      buttonText: t("pricingSection.plans.free.btn"),
      popular: false
    },
    {
      name: t("pricingSection.plans.starter.name"),
      desc: t("pricingSection.plans.starter.desc"),
      price: "19.99",
      features: [
        t("pricingSection.plans.starter.feat1"),
        t("pricingSection.plans.starter.feat2"),
        t("pricingSection.plans.starter.feat3"),
        t("pricingSection.plans.starter.feat4"),
        t("pricingSection.plans.starter.feat5"),
        t("pricingSection.plans.starter.feat6"),
        t("pricingSection.plans.starter.feat7")
      ],
      buttonText: t("pricingSection.plans.starter.btn"),
      popular: false
    },
    {
      name: t("pricingSection.plans.premium.name"),
      desc: t("pricingSection.plans.premium.desc"),
      price: "59.99",
      features: [
        t("pricingSection.plans.premium.feat1"),
        t("pricingSection.plans.premium.feat2"),
        t("pricingSection.plans.premium.feat3"),
        t("pricingSection.plans.premium.feat4"),
        t("pricingSection.plans.premium.feat5"),
        t("pricingSection.plans.premium.feat6")
      ],
      buttonText: t("pricingSection.plans.premium.btn"),
      popular: true
    },
    {
      name: t("pricingSection.plans.professional.name"),
      desc: t("pricingSection.plans.professional.desc"),
      price: "199.99",
      features: [
        t("pricingSection.plans.professional.feat1"),
        t("pricingSection.plans.professional.feat2"),
        t("pricingSection.plans.professional.feat3"),
        t("pricingSection.plans.professional.feat4"),
        t("pricingSection.plans.professional.feat5"),
        t("pricingSection.plans.professional.feat6"),
        t("pricingSection.plans.professional.feat7")
      ],
      buttonText: t("pricingSection.plans.professional.btn"),
      popular: false
    }
  ];

  const comparisonData = [
    {
      category: t("pricingSection.comparison.sales"),
      features: [
        { name: t("pricingSection.comparison.freeTickets"), values: [true, true, true, true] },
        { name: t("pricingSection.comparison.paidTickets"), values: [false, true, true, true] },
        { name: t("pricingSection.comparison.txFee"), values: [txFee("0", 0), txFee("3.99", 0.69), txFee("2.99", 0.49), txFee("1.99", 0.29)] },
        { name: t("pricingSection.comparison.promoCodes"), values: [false, true, true, true] },
        { name: t("pricingSection.comparison.waitlist"), values: [false, false, true, true] },
        { name: t("pricingSection.comparison.contactlessQR"), values: [false, true, true, true] },
        { name: t("pricingSection.comparison.donationPayments"), values: [false, false, true, true] },
        { name: t("pricingSection.comparison.nonprofitDiscount"), values: [false, true, true, true] },
      ]
    },
    {
      category: t("pricingSection.comparison.operations"),
      features: [
        { name: t("pricingSection.comparison.payoutFreq"), values: [t("pricingSection.comparison.none"), t("pricingSection.comparison.monthly"), t("pricingSection.comparison.biweekly"), t("pricingSection.comparison.daily")] },
        { name: t("pricingSection.comparison.phoneColl"), values: [false, true, true, true] },
        { name: t("pricingSection.comparison.aiChat"), values: [false, false, true, true] },
        { name: t("pricingSection.comparison.domain"), values: [false, false, false, true] },
        { name: t("pricingSection.comparison.whiteLabel"), values: [false, false, false, t("pricingSection.comparison.available")] },
      ]
    },
    {
      category: t("pricingSection.comparison.support"),
      features: [
        { name: t("pricingSection.comparison.emailSupp"), values: [true, true, true, true] },
        { name: t("pricingSection.comparison.spec"), values: [false, false, true, true] },
        { name: t("pricingSection.comparison.sla"), values: [false, false, false, true] },
      ]
    }
  ];

  return (
    <section id="pricing" className="py-24 bg-slate-50">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold uppercase tracking-wider mb-4">
            {t('pricingSection.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
            {t('pricingSection.title')}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('pricingSection.desc')}
          </p>

          {/* USD / AUD currency toggle */}
          <div className="flex justify-center mt-6 mb-12">
            <div className="inline-flex gap-1 bg-white border border-slate-200 rounded-full p-1 shadow-sm">
              {['EUR', 'USD', 'AUD'].map((cur) => (
                <button
                  key={cur}
                  onClick={() => {
                    if (currency === cur) return;
                    trackEvent('currency_toggle', { currency: cur });
                    setCurrency(cur);
                  }}
                  className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${
                    currency === cur
                      ? 'bg-indigo-600 text-white shadow'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {cur}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-8 mb-12">
          {plans.map((plan, idx) => (
            <div 
              key={idx} 
              className={`relative bg-white rounded-3xl p-8 flex flex-col transition-all duration-300 ${
                plan.popular 
                  ? 'border-2 border-indigo-500 shadow-2xl shadow-indigo-500/20 xl:scale-105 z-10' 
                  : 'border border-slate-200 shadow-sm hover:shadow-xl'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-primary text-white text-xs font-bold uppercase tracking-wider py-1 px-4 rounded-full">
                  {t('pricingSection.popular')}
                </div>
              )}
              
              <div className="mb-8">
                <h3 className="text-xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                <p className="text-sm text-slate-500 h-10">{plan.desc}</p>
              </div>

              <div className="mb-8 flex items-baseline gap-1">
                <span className="text-slate-500 text-xl font-semibold">{symbol}</span>
                <span className="text-4xl font-extrabold text-slate-900 tracking-tight">{planPrice(plan.price)}</span>
                <span className="text-slate-500 font-medium">{t('pricingSection.perMonth')}</span>
              </div>

              <ul className="space-y-4 mb-8 flex-1">
                {plan.features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                    <SafeIcon name="Check" className="w-5 h-5 text-indigo-500 shrink-0" />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>

              <button 
                onClick={() => trackEvent('select_plan', { plan_name: plan.name, plan_price: plan.price })}
                className={`w-full py-3 px-4 rounded-xl font-semibold transition-all ${
                  plan.popular 
                    ? 'bg-gradient-primary text-white shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transform hover:-translate-y-0.5' 
                    : 'bg-slate-50 text-slate-900 border border-slate-200 hover:bg-slate-100'
                }`}
              >
                {plan.buttonText}
              </button>
            </div>
          ))}
        </div>

        {/* Collapsible Comparison Table */}
        <div className="mt-8 mb-16 text-center">
          {/* Spinning conic-gradient ring wrapper */}
          <div className="relative inline-flex">
            <div
              className="absolute -inset-[1.5px] z-0 pointer-events-none"
              style={{
                borderRadius: '14px',
                background: 'conic-gradient(from var(--angle-cmp, 0deg), #6366f1, #8b5cf6, #ec4899, #f59e0b, #6366f1)',
                animation: 'spin-border-cmp 2.5s linear infinite',
              }}
            />
            <style dangerouslySetInnerHTML={{ __html: `
              @property --angle-cmp {
                syntax: '<angle>';
                initial-value: 0deg;
                inherits: false;
              }
              @keyframes spin-border-cmp {
                to { --angle-cmp: 360deg; }
              }
            `}} />
            <button
              onClick={() => {
                const nextState = !showComparison;
                trackEvent('toggle_comparison_table', { visible: nextState });
                setShowComparison(nextState);
              }}
              className="relative z-10 p-[1.5px] inline-flex items-center justify-center font-bold rounded-xl overflow-hidden group bg-gradient-primary hover:shadow-lg hover:shadow-indigo-500/20 transition-all cursor-pointer"
            >
              <span className="relative px-6 py-3 transition-all duration-300 bg-slate-50 rounded-[11px] group-hover:bg-transparent text-indigo-600 group-hover:text-white flex items-center gap-2">
                {showComparison ? t('pricingSection.hideComparison') : t('pricingSection.showComparison')}
                <SafeIcon name="ChevronDown" className={`w-4.5 h-4.5 transition-transform duration-300 ${showComparison ? 'rotate-180' : ''}`} />
              </span>
            </button>
          </div>

          <AnimatePresence>
            {showComparison && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-12 overflow-x-auto pb-8">
                  <table className="w-full text-left border-collapse bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-200">
                    <thead>
                      <tr className="bg-slate-900 text-white">
                        <th className="p-6 text-sm font-bold uppercase tracking-wider">{t('pricingSection.thFeatures')}</th>
                        <th className="p-6 text-sm font-bold uppercase tracking-wider text-center">{t('pricingSection.plans.free.name')}</th>
                        <th className="p-6 text-sm font-bold uppercase tracking-wider text-center">{t('pricingSection.plans.starter.name')}</th>
                        <th className="p-6 text-sm font-bold uppercase tracking-wider text-center">{t('pricingSection.plans.premium.name')}</th>
                        <th className="p-6 text-sm font-bold uppercase tracking-wider text-center">{t('pricingSection.plans.professional.name')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.map((cat, catIdx) => (
                        <React.Fragment key={catIdx}>
                          <tr className="bg-slate-50 border-y border-slate-200">
                            <td colSpan="5" className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-widest text-left">
                              {cat.category}
                            </td>
                          </tr>
                          {cat.features.map((feature, fIdx) => (
                            <tr key={fIdx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                              <td className="p-6 text-sm font-medium text-slate-700">{feature.name}</td>
                              {feature.values.map((val, vIdx) => (
                                <td key={vIdx} className="p-6 text-center">
                                  {typeof val === 'boolean' ? (
                                    val ? (
                                      <SafeIcon name="Check" className="w-5 h-5 text-emerald-500 mx-auto" />
                                    ) : (
                                      <SafeIcon name="X" className="w-5 h-5 text-slate-300 mx-auto" />
                                    )
                                  ) : (
                                    <span className="text-sm font-semibold text-slate-900">{val}</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Enterprise/White-label Card */}
        <div className="bg-slate-900 rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 border border-slate-800 relative overflow-hidden mb-12">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 pointer-events-none"></div>
          
          <div className="relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4 border border-slate-700">
              <SafeIcon name="Shield" className="w-4 h-4" /> {t('pricingSection.enterpriseBadge')}
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">{t('pricingSection.enterpriseTitle')}</h3>
            <p className="text-slate-400 mb-6 text-sm md:text-base">
              {t('pricingSection.enterpriseDesc')}
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-slate-300">
              <span className="flex items-center gap-2"><SafeIcon name="Check" className="w-4 h-4 text-emerald-400"/> {t('pricingSection.enterpriseFeat1')}</span>
              <span className="flex items-center gap-2"><SafeIcon name="Check" className="w-4 h-4 text-emerald-400"/> {t('pricingSection.enterpriseFeat2')}</span>
              <span className="flex items-center gap-2"><SafeIcon name="Check" className="w-4 h-4 text-emerald-400"/> {t('pricingSection.enterpriseFeat3')}</span>
            </div>

            <div className="flex items-center gap-4 mt-8 pt-6 border-t border-slate-800">
              <div className="flex -space-x-2">
                 <FaceAvatar index={6} className="w-8 h-8 rounded-full border-2 border-slate-900" />
                 <FaceAvatar index={7} className="w-8 h-8 rounded-full border-2 border-slate-900" />
                 <FaceAvatar index={8} className="w-8 h-8 rounded-full border-2 border-slate-900" />
              </div>
              <span className="text-xs text-slate-400 font-medium">{t('pricingSection.enterpriseTrust')}</span>
            </div>
          </div>

          <div className="relative z-10 shrink-0 w-full md:w-auto">
            <button 
              onClick={() => {
                trackEvent('select_plan', { plan_name: 'Enterprise', plan_price: 'Custom' });
                setIsEnterpriseModalOpen(true);
              }}
              className="w-full md:w-auto bg-white text-slate-900 px-8 py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors"
            >
              <SafeIcon name="Briefcase" className="w-5 h-5" /> {t('pricingSection.enterpriseCta')}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isEnterpriseModalOpen && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            {/* Backdrop - Note: Clicking outside does NOT close the modal */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="relative bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden z-10"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                    <SafeIcon name="Briefcase" className="w-4.5 h-4.5" />
                  </div>
                  <h3 className="font-extrabold text-slate-900 text-lg">
                    {t('pricingSection.enterpriseCta')}
                  </h3>
                </div>
                
                <div className="flex items-center gap-3">
                  <a 
                    href="https://survey.bookmein.events/JoDAmB" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center"
                    title="Open in new tab"
                  >
                    <SafeIcon icon={FiExternalLink} className="w-5 h-5" />
                  </a>
                  <button
                    type="button"
                    onClick={() => setIsEnterpriseModalOpen(false)}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center focus:outline-none"
                    title="Close"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Webview / Iframe container */}
              <div className="flex-1 bg-slate-50 relative">
                <iframe
                  src="https://survey.bookmein.events/JoDAmB"
                  title="Book Enterprise Demo"
                  className="w-full h-full border-0 absolute inset-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Pricing;